<script>
export default {
	name: 'HiddenSeamlessBookingWidget',
	inheritAttrs: false,
};
</script>

<script setup>
import CheckIn from '~/aem-components/booking/CheckIn.vue';
import FlightHotelSearch from '~/aem-components/booking/FlightHotelSearch.vue';
import FlightSchedule from '~/aem-components/booking/FlightSchedule.vue';
import FlightSearch from '~/aem-components/booking/FlightSearch.vue';
import FlightStatus from '~/aem-components/booking/FlightStatus.vue';
import ManageBooking from '~/aem-components/booking/ManageBooking.vue';
import MHupgrade from '~/aem-components/booking/MHupgrade.vue';
import axios from 'axios';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { waitFor } from '~/logic/helpers/utils.js';
import { setCookie } from '~/logic/helpers/cookies.js';

const checkInPropsFromAPI = ref(null);
const flightSearchHotelPropsFromAPI = ref(null);
const flightSchedulePropsFromAPI = ref(null);
const flightSearchPropsFromAPI = ref(null);
const flightStatusPropsFromAPI = ref(null);
const manageBookingPropsFromAPI = ref(null);
const upgradePropsFromAPI = ref(null);

const bookingWidgetArrayList = [
	{
		componentName: 'check-in',
		dataRef: checkInPropsFromAPI,
		key: 'check-in',
	},
	{
		componentName: 'flight-hotel-search',
		dataRef: flightSearchHotelPropsFromAPI,
		key: 'flight-hotel',
	},
	{
		componentName: 'flight-schedule',
		dataRef: flightSchedulePropsFromAPI,
		key: 'flight-schedule',
	},
	{
		componentName: 'flight-search',
		dataRef: flightSearchPropsFromAPI,
		key: 'flight-search',
	},
	{
		componentName: 'flight-status',
		dataRef: flightStatusPropsFromAPI,
		key: 'flight-status',
	},
	{
		componentName: 'manage-booking',
		dataRef: manageBookingPropsFromAPI,
		key: 'manage-booking',
	},
	{
		componentName: 'mh-upgrade',
		dataRef: upgradePropsFromAPI,
		key: 'mh-upgrade',
	},
];

// Methods definitions

function transformObjectProps (keyProps = ['isHideInDesktop'], dataProps = {}) {
	const dataPropsKey = Object.keys(dataProps);

	for (const key of dataPropsKey) {
		for (const k of keyProps) {
			if (Object.hasOwn(dataProps, k)) {
				dataProps[k] = true;
			}
		}

		if (typeof dataProps[key] === 'object' && dataProps[key] !== null) {
			transformObjectProps(keyProps, dataProps[key]);
		}
	}

	return dataProps;
}

const setupProps = async (subBookingWidgetArray = []) => {
	const { pageProperties } = useGlobalAEMState();
	const country = pageProperties.value.rootCountry;
	let language = pageProperties.value.rootLanguage;
	
	// !!Note: a special handler only for CN site, as the root page language is being setup using special-letter-case
	if (language === 'zh_cn') {
		language = 'zh_CN';
	}

	let filteredBookingWidgets = null;
	const listOfWidgetsConfigured = await axios.get(`/content/mh/${country}/${language}/home/jcr:content/root/embedded_booking_widget.widgetdata.json`);
	
	if (listOfWidgetsConfigured?.data?.mobileProperties) {
		const updatedArray = listOfWidgetsConfigured.data.mobileProperties.map(({ urlPath, isHideInMobile, ctaText, ...rest }) => {
			const [, hash = ''] = urlPath.split('#');

			return {
				...rest,
				urlPath: hash ? `#${hash}` : '',
				id: hash ? `#${hash}` : '',
				label: ctaText,
			};
		});
		setCookie('mobileProperties', JSON.stringify(updatedArray));
	}
	if (listOfWidgetsConfigured?.data?.desktopProperties) {
		const configuredFunctions = new Set((listOfWidgetsConfigured.data.desktopProperties).map((item) => item.function));
		filteredBookingWidgets = bookingWidgetArrayList.filter((widget) => configuredFunctions.has(widget.key));
	}
	if (filteredBookingWidgets === null || filteredBookingWidgets.length === 0) {
		filteredBookingWidgets = subBookingWidgetArray;
	}

	try {
		filteredBookingWidgets.forEach(async (item, index) => {
			const resp = await axios.get(`/content/mh/${country}/${language}/home/jcr:content/root/embedded_booking_widget/${item.componentName}.model.json`);
			item.dataRef.value = resp.data;
		});
	} catch (error) {
		console.error(error);
	}
};


// Computed properties

const checkInProps = computed(() => {
	
	if (checkInPropsFromAPI.value === null) return null;
	
	return {
		...checkInPropsFromAPI.value,
		isHideInDesktop: true,
	};
});

const flightHotelSearchProps = computed(() => {
	
	if (flightSearchHotelPropsFromAPI.value === null) return null;
	
	return {
		...transformObjectProps(['isHideInDesktop'], flightSearchHotelPropsFromAPI.value),
	};
});

const flightScheduleProps = computed(() => {
	
	if (flightSchedulePropsFromAPI.value === null) return null;
	
	return {
		...flightSchedulePropsFromAPI.value,
		isHideInDesktop: true,
	};
});

const flightSearchProps = computed(() => {
	
	if (flightSearchPropsFromAPI.value === null) return null;
	
	return {
		...transformObjectProps(['isHideInDesktop', 'isHideInDesktopRedeemFlight'], flightSearchPropsFromAPI.value),
		isHideContinueYourSearchSection: true,
	};
});

const flightStatusProps = computed(() => {
	
	if (flightStatusPropsFromAPI.value === null) return null;
	
	return {
		...transformObjectProps(['isHideInDesktop'], flightStatusPropsFromAPI.value),
	};
});

const manageBookingProps = computed(() => {
	
	if (manageBookingPropsFromAPI.value === null) return null;
	
	return {
		...transformObjectProps(['isHideInDesktop'], manageBookingPropsFromAPI.value),
	};
});

const upgradeProps = computed(() => {
	
	if (upgradePropsFromAPI.value === null) return null;
	
	return {
		...upgradePropsFromAPI.value,
		isHideInDesktop: true,
	};
});

const getElementIfExist = async (selector) => {
	// await nextTick();
	try {
		const wait = waitFor(() => document.querySelector(selector) !== null, 100, 6);
		await wait.start();
		
		const el = document.querySelector(selector);
		if (el) return true;
		
	} catch (error) {
		// if (error) return;
	}
	
	return false;
};

const isCheckInMounted = ref(true);
const isFlightHotelSearchMounted = ref(true);
const isFlightScheduleMounted = ref(true);
const isFlightSearchMounted = ref(true);
const isFlightStatusMounted = ref(true);
const isManageBookingMounted = ref(true);
const isUpgradeMounted = ref(true);

onMounted(async () => {
	setupProps(bookingWidgetArrayList);
	
	isCheckInMounted.value = await getElementIfExist('.CheckIn');
	isFlightHotelSearchMounted.value = await getElementIfExist('.FlightHotelSearch');
	isFlightScheduleMounted.value = await getElementIfExist('.FlightSchedule');
	isFlightSearchMounted.value = await getElementIfExist('.FlightSearch');
	isFlightStatusMounted.value = await getElementIfExist('.FlightStatus');
	isManageBookingMounted.value = await getElementIfExist('.ManageBooking');
	isUpgradeMounted.value = await getElementIfExist('.MHupgrade');
});

</script>

<template>
<div class="HiddenSeamlessBookingWidget">
	<template v-if="isMobileViewport">
		<CheckIn
			v-if="!isCheckInMounted && checkInProps !== null"
			v-bind="checkInProps"
		/>
		<FlightHotelSearch
			v-if="!isFlightHotelSearchMounted && flightHotelSearchProps !== null"
			v-bind="flightHotelSearchProps"
		/>
		<FlightSchedule
			v-if="!isFlightScheduleMounted && flightScheduleProps !== null"
			v-bind="flightScheduleProps"
		/>
		<FlightSearch
			v-if="!isFlightSearchMounted && flightSearchProps !== null"
			v-bind="flightSearchProps"
		/>
		<FlightStatus
			v-if="!isFlightStatusMounted && flightStatusProps !== null"
			v-bind="flightStatusProps"
		/>
		<ManageBooking
			v-if="!isManageBookingMounted && manageBookingProps !== null"
			v-bind="manageBookingProps"
		/>
		<MHupgrade
			v-if="!isUpgradeMounted && upgradeProps !== null"
			v-bind="upgradeProps"
		/>
	</template>
</div>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.HiddenSeamlessBookingWidget {
	
}

</style>